import type { CampaignScheduleImportResult } from '~/hooks/apis/offers/useImportCampaignSchedules';

import type { CampaignScheduleImportCandidate } from './types';

export interface CampaignScheduleCandidateGroup {
  key: string;
  candidates: Array<CampaignScheduleImportCandidate>;
  regions: Array<string>;
}

function resultKey(result: CampaignScheduleImportResult | undefined): string {
  if (!result) {
    return 'pending';
  }

  return result.success ? 'success' : `error:${result.error}`;
}

// Structural key from dependencyViolation's own fields — same violation always shares a key.
function dependencyViolationKey(candidate: CampaignScheduleImportCandidate): string {
  const violation = candidate.dependencyViolation;
  return violation ? `${violation.code}::${violation.candidateType}::${violation.requiredType}` : '';
}

// Groups candidates that would render identically except for region, so the modal shows one row with a
// region pill/dropdown per group; any divergence (conflict, result) naturally splits its own group.
export function groupCandidatesByRegion(
  candidates: Array<CampaignScheduleImportCandidate>,
  resultByKey: Map<string, CampaignScheduleImportResult>,
): Array<CampaignScheduleCandidateGroup> {
  const groups = new Map<string, CampaignScheduleCandidateGroup>();

  candidates.forEach((candidate) => {
    const conflict = candidate.conflictingSchedule;
    const groupKey = [
      candidate.parentRowId,
      candidate.brand,
      candidate.isMatched,
      dependencyViolationKey(candidate),
      conflict ? `${conflict.start}|${conflict.end}` : '',
      resultKey(resultByKey.get(candidate.key)),
    ].join('::');

    const existing = groups.get(groupKey);

    if (existing) {
      existing.candidates.push(candidate);
      existing.regions.push(candidate.marketLabel);
    } else {
      groups.set(groupKey, { key: groupKey, candidates: [candidate], regions: [candidate.marketLabel] });
    }
  });

  return Array.from(groups.values());
}
